import { randomNumber } from '@perch-labs/warbler';
import { FetchUtilityResponse } from '~/services/fetch-utility';
import { FunnelTrack, UtilityAccountFieldType } from '~/typings/graphql';
import { STATE_IDS, UTILITY_IDS } from '~/utils/constants';

/**
 *  Generates a random utility account number for testing purposes.
 */
export const randomAccountNumber = (): string => {
  return randomNumber(10).toString();
};

const vnmElegibleUtilityProducts = [
  {
    id: 3,
    productType: 'BillPayService',
  },
  {
    id: 178,
    productType: 'Wind',
  },
  {
    id: 177,
    productType: 'Wind',
  },
  {
    id: 8,
    productType: 'VNM',
  },
  {
    id: 312,
    productType: 'Funding',
  },
  {
    id: 212,
    productType: 'Membership',
  },
];

export const TEST_STATES = {
  ca: {
    abbreviation: 'CA',
    requireCommunicationPreference: false,
    id: STATE_IDS.CA,
    name: 'California',
  },
  co: {
    abbreviation: 'CO',
    requireCommunicationPreference: false,
    id: STATE_IDS.CO,
    name: 'Colorado',
  },
  dc: { abbreviation: 'DC', requireCommunicationPreference: false, id: STATE_IDS.DC, name: 'DC' },
  il: {
    abbreviation: 'IL',
    requireCommunicationPreference: false,
    id: STATE_IDS.IL,
    name: 'Illinois',
  },
  ma: {
    abbreviation: 'MA',
    requireCommunicationPreference: false,
    id: STATE_IDS.MA,
    name: 'Massachusetts',
  },
  md: {
    abbreviation: 'MD',
    requireCommunicationPreference: false,
    id: STATE_IDS.MD,
    name: 'Maryland',
  },
  me: {
    abbreviation: 'ME',
    requireCommunicationPreference: false,
    id: STATE_IDS.ME,
    name: 'Maine',
  },
  nj: {
    abbreviation: 'NJ',
    requireCommunicationPreference: false,
    id: STATE_IDS.NJ,
    name: 'New Jersey',
  },
  nm: {
    abbreviation: 'NM',
    requireCommunicationPreference: false,
    id: STATE_IDS.NJ,
    name: 'New Mexico',
  },
  mn: {
    abbreviation: 'MN',
    requireCommunicationPreference: false,
    id: STATE_IDS.MN,
    name: 'Minnesota',
  },
  ny: {
    abbreviation: 'NY',
    requireCommunicationPreference: false,
    id: STATE_IDS.NY,
    name: 'New York',
  },
  or: {
    abbreviation: 'OR',
    requireCommunicationPreference: true,
    id: STATE_IDS.OR,
    name: 'Oregon',
  },
  ri: {
    abbreviation: 'RI',
    requireCommunicationPreference: false,
    id: STATE_IDS.RI,
    name: 'Rhode Island',
  },
  va: {
    abbreviation: 'VA',
    requireCommunicationPreference: false,
    id: STATE_IDS.VA,
    name: 'Virginia',
  },
  oh: { abbreviation: 'OH', requireCommunicationPreference: false, id: STATE_IDS.OH, name: 'Ohio' },
  de: {
    abbreviation: 'DE',
    requireCommunicationPreference: false,
    id: STATE_IDS.DE,
    name: 'Delaware',
  },
};

export const TEST_UTILITIES = {
  alameda: {
    id: UTILITY_IDS['Alameda Municipal Power'],
    displayName: 'Alameda Municipal Power',
    credentialType: 'UsernamePassword',
    emailIsUsername: false,
    funnelTrack: 'membership' as FunnelTrack,
    state: TEST_STATES.ca,
    zipCode: '94501',
    products: [
      {
        id: 177,
        productType: 'Wind',
      },
      {
        id: 312,
        productType: 'Funding',
      },
      {
        id: 212,
        productType: 'Membership',
      },
    ],
    fieldValidationRules: [],
  },
  ameren: {
    id: UTILITY_IDS['Ameren IL'],
    funnelTrack: 'solar' as FunnelTrack,
    displayName: 'Ameren',
    emailIsUsername: false,
    credentialType: 'APIToken',
    state: TEST_STATES.il,
    zipCode: '60479',
    products: vnmElegibleUtilityProducts,
    fieldValidationRules: [
      {
        id: 1,
        fieldName: 'Meter number',
        fieldType: UtilityAccountFieldType.MeterNumber,
        patterns: ['^\\d{8}$'],
        helpText: 'Please enter a valid Ameren IL meter number (8 digits)',
      },
    ],
  },
  amerenMo: {
    id: UTILITY_IDS['Ameren MO'],
    funnelTrack: 'solar' as FunnelTrack,
    displayName: 'Ameren',
    emailIsUsername: false,
    credentialType: 'APIToken',
    state: TEST_STATES.il,
    zipCode: '63005',
    products: vnmElegibleUtilityProducts,
    fieldValidationRules: [],
  },
  centralHudson: {
    id: UTILITY_IDS['Central Hudson Gas & Electric'],
    displayName: 'Central Hudson',
    emailIsUsername: true,
    funnelTrack: 'solar' as FunnelTrack,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.ny,
    zipCode: '10516',
    products: vnmElegibleUtilityProducts,
    fieldValidationRules: [],
  },
  centralMainePower: {
    id: UTILITY_IDS['Central Maine Power'],
    displayName: 'Central Maine Power',
    emailIsUsername: false,
    funnelTrack: 'solar' as FunnelTrack,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.me,
    zipCode: '03901',
    products: vnmElegibleUtilityProducts,
    fieldValidationRules: [],
  },
  cmp: {
    id: UTILITY_IDS['Central Maine Power'],
    displayName: 'Central Maine Power',
    emailIsUsername: false,
    funnelTrack: 'solar' as FunnelTrack,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.ma,
    zipCode: '04330',
    products: vnmElegibleUtilityProducts,
    fieldValidationRules: [],
  },
  conEd: {
    id: UTILITY_IDS['Consolidated Edison NY'],
    displayName: 'ConEd',
    emailIsUsername: false,
    funnelTrack: 'solar' as FunnelTrack,
    credentialType: 'PortalIdentifier',
    state: TEST_STATES.ny,
    zipCode: '10001',
    products: vnmElegibleUtilityProducts,
    fieldValidationRules: [],
  },
  comEd: {
    id: UTILITY_IDS['Commonwealth Edison'],
    funnelTrack: 'solar' as FunnelTrack,
    displayName: 'ComEd',
    emailIsUsername: true,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.il,
    zipCode: '60002',
    products: vnmElegibleUtilityProducts,
    fieldValidationRules: [
      {
        id: 2,
        fieldName: 'Meter number',
        fieldType: UtilityAccountFieldType.MeterNumber,
        patterns: ['^\\d{9}$'],
        helpText: 'Please enter a valid Commonwealth Edison meter number (9 digits)',
      },
      {
        id: 3,
        fieldName: 'Electric Choice ID',
        fieldType: UtilityAccountFieldType.SecondaryAccountIdentifier,
        patterns: ['^[0-9]{10}$'],
        helpText:
          'Please check the top of the second page of your latest ComEd bill to find your 10-digit Electric Choice ID.',
      },
    ],
  },
  ohioEdison: {
    id: UTILITY_IDS['Ohio Edison'],
    funnelTrack: 'solar' as FunnelTrack,
    displayName: 'Ohio Edison',
    emailIsUsername: true,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.oh,
    zipCode: '43010',
    products: vnmElegibleUtilityProducts,
    fieldValidationRules: [],
  },
  aepOh: {
    id: UTILITY_IDS['AEP OH'],
    funnelTrack: 'solar' as FunnelTrack,
    displayName: 'AEP OH',
    emailIsUsername: true,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.oh,
    zipCode: '43001',
    products: vnmElegibleUtilityProducts,
    fieldValidationRules: [],
  },
  dominion: {
    id: UTILITY_IDS['Dominion Energy VA'],
    funnelTrack: 'solar' as FunnelTrack,
    displayName: 'Dominion',
    emailIsUsername: false,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.va,
    zipCode: '20105',
    products: vnmElegibleUtilityProducts,
    fieldValidationRules: [
      {
        id: 3,
        fieldName: 'Meter number',
        fieldType: UtilityAccountFieldType.MeterNumber,
        patterns: ['^\\d{10}$'],
        helpText: 'Please enter a valid Dominion Energy VA meter number (10 digits)',
      },
    ],
  },
  eversource: {
    id: UTILITY_IDS['Eversource / NSTAR'],
    displayName: 'Eversource NSTAR',
    emailIsUsername: false,
    funnelTrack: 'solar' as FunnelTrack,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.ma,
    zipCode: '02446',
    products: vnmElegibleUtilityProducts,
    fieldValidationRules: [
      {
        id: 4,
        fieldName: 'Meter number',
        fieldType: UtilityAccountFieldType.MeterNumber,
        patterns: ['^\\d{7}$'],
        helpText: 'Please enter a valid Eversource meter number (7 digits)',
      },
    ],
  },
  eversourceWmeco: {
    id: UTILITY_IDS['Eversource / WMECO'],
    displayName: 'Eversource WMECO',
    emailIsUsername: false,
    funnelTrack: 'solar' as FunnelTrack,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.ma,
    zipCode: '01001',
    products: vnmElegibleUtilityProducts,
    fieldValidationRules: [],
  },
  jcpl: {
    id: UTILITY_IDS['Jersey Central Power & Light'],
    funnelTrack: 'solar' as FunnelTrack,
    displayName: 'JCP&L',
    emailIsUsername: false,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.nj,
    zipCode: '07005',
    products: vnmElegibleUtilityProducts,
    fieldValidationRules: [
      {
        id: 10,
        fieldName: 'Customer Number',
        fieldType: UtilityAccountFieldType.SecondaryAccountIdentifier,
        patterns: ['^[0-9]{20}$'],
        helpText: 'This 20 digit ID can be found on the third page of your utility bill',
      },
      {
        id: 11,
        fieldName: 'Meter number',
        fieldType: UtilityAccountFieldType.MeterNumber,
        patterns: ['^\\d{10}$'],
        helpText: 'Please enter a valid meter number',
      },
    ],
  },
  natGrid: {
    id: UTILITY_IDS['National Grid NY'],
    funnelTrack: 'solar' as FunnelTrack,
    displayName: 'National Grid',
    emailIsUsername: true,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.ny,
    zipCode: '13327',
    products: vnmElegibleUtilityProducts,
    fieldValidationRules: [],
  },
  natGridMa: {
    id: UTILITY_IDS['National Grid MA'],
    funnelTrack: 'solar' as FunnelTrack,
    displayName: 'National Grid',
    emailIsUsername: true,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.ma,
    zipCode: '01005',
    products: vnmElegibleUtilityProducts,
    fieldValidationRules: [],
  },
  unitilMa: {
    id: UTILITY_IDS['Unitil MA'],
    funnelTrack: 'solar' as FunnelTrack,
    displayName: 'Unitil MA',
    emailIsUsername: true,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.ma,
    zipCode: '01431',
    products: vnmElegibleUtilityProducts,
    fieldValidationRules: [],
  },
  nySeg: {
    id: UTILITY_IDS.NYSEG,
    displayName: 'NYSEG',
    emailIsUsername: false,
    funnelTrack: 'solar' as FunnelTrack,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.ny,
    zipCode: '12421',
    products: vnmElegibleUtilityProducts,
    fieldValidationRules: [
      {
        id: 99,
        fieldName: 'PoD ID',
        fieldType: UtilityAccountFieldType.SecondaryAccountIdentifier,
        patterns: ['^N[0-9]{14}$'],
        helpText:
          'This 15 digit ID starts with the letter N. It can be found on the third page of your utility bill.',
      },
    ],
  },
  orange: {
    id: UTILITY_IDS['Orange and Rockland'],
    displayName: 'Orange and Rockland',
    emailIsUsername: false,
    funnelTrack: 'solar' as FunnelTrack,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.ny,
    zipCode: '10901',
    products: [
      {
        id: 177,
        productType: 'Wind',
      },
      {
        id: 312,
        productType: 'Funding',
      },
      {
        id: 212,
        productType: 'Membership',
      },
    ],
    fieldValidationRules: [],
  },
  pepco: {
    id: UTILITY_IDS['PEPCO MD'],
    displayName: 'Pepco',
    emailIsUsername: false,
    funnelTrack: 'solar' as FunnelTrack,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.md,
    zipCode: '20814',
    products: vnmElegibleUtilityProducts,
    fieldValidationRules: [],
  },
  pepcodc: {
    id: UTILITY_IDS['PEPCO DC'],
    displayName: 'Pepco',
    emailIsUsername: false,
    funnelTrack: 'solar' as FunnelTrack,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.dc,
    zipCode: '20001',
    products: vnmElegibleUtilityProducts,
    fieldValidationRules: [],
  },
  pnm: {
    id: UTILITY_IDS['Public Service Company of NM'],
    displayName: 'Public Service Company of NM',
    emailIsUsername: false,
    funnelTrack: FunnelTrack.Waitlist,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.nm,
    zipCode: '87001',
    products: vnmElegibleUtilityProducts,
    fieldValidationRules: [],
  },
  portlandGE: {
    id: UTILITY_IDS['Portland General Electric'],
    funnelTrack: 'solar' as FunnelTrack,
    displayName: 'Portland General Electric',
    emailIsUsername: true,
    credentialType: 'UsernamePassword',
    state: {
      abbreviation: 'OR',
      requireCommunicationPreference: true,
      id: 38,
      name: 'Oregon',
    },
    zipCode: '97002',
    products: vnmElegibleUtilityProducts,
    fieldValidationRules: [
      {
        id: 5,
        fieldName: 'Meter number',
        fieldType: UtilityAccountFieldType.MeterNumber,
        patterns: ['^\\d{8}[A-Z]{2}$'],
        helpText:
          'Please enter a valid PGE meter number (8 digits followed by 2 Uppercase letters)',
      },
    ],
  },
  psegNj: {
    id: UTILITY_IDS['PSE&G NJ'],
    funnelTrack: 'solar' as FunnelTrack,
    displayName: 'PSE&G',
    emailIsUsername: false,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.nj,
    zipCode: '07001',
    products: vnmElegibleUtilityProducts,
    fieldValidationRules: [],
  },
  sdge: {
    id: UTILITY_IDS['SDG&E'],
    funnelTrack: 'membership' as FunnelTrack,
    displayName: 'SDG&E',
    emailIsUsername: false,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.ca,
    zipCode: '92116',
    products: vnmElegibleUtilityProducts,
    fieldValidationRules: [],
  },
  xcelNM: {
    id: UTILITY_IDS['Xcel Energy NM'],
    funnelTrack: 'solar' as FunnelTrack,
    displayName: 'Xcel Energy NM',
    emailIsUsername: false,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.nm,
    zipCode: '88101',
    products: vnmElegibleUtilityProducts,
    fieldValidationRules: [
      {
        id: 8,
        fieldName: 'Meter number',
        fieldType: UtilityAccountFieldType.MeterNumber,
        patterns: ['^\\d{8,9}$'],
        helpText: 'Please enter a valid meter number',
      },
    ],
  },
  xcelMN: {
    id: UTILITY_IDS['Xcel Energy MN'],
    funnelTrack: 'solar' as FunnelTrack,
    displayName: 'Xcel Energy',
    emailIsUsername: false,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.mn,
    zipCode: '55001',
    products: vnmElegibleUtilityProducts,
    fieldValidationRules: [
      {
        id: 6,
        fieldName: 'Premises Number',
        fieldType: UtilityAccountFieldType.SecondaryAccountIdentifier,
        patterns: ['^[0-9]{9}$'],
        helpText:
          'The 9-digit premises number for your Xcel account can be found on the bill under "Electricity Service Details"',
      },
      {
        id: 7,
        fieldName: 'Meter number',
        fieldType: UtilityAccountFieldType.MeterNumber,
        patterns: ['^\\d{8,9}$'],
        helpText: 'Please enter a valid Xcel Energy MN meter number (8 or 9 digits)',
      },
    ],
  },
  xcelCO: {
    id: UTILITY_IDS['Xcel Energy CO'],
    funnelTrack: 'solar' as FunnelTrack,
    displayName: 'Xcel Energy CO',
    emailIsUsername: false,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.co,
    zipCode: '88101',
    products: vnmElegibleUtilityProducts,
    fieldValidationRules: [],
  },
  delmarvaDE: {
    id: UTILITY_IDS['Delmarva Power DE'],
    funnelTrack: 'solar' as FunnelTrack,
    displayName: 'Delmarva Power',
    emailIsUsername: false,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.de,
    zipCode: '19701',
    products: vnmElegibleUtilityProducts,
    fieldValidationRules: [],
  },
  xcelEnergyCO: {
    id: UTILITY_IDS['Xcel Energy CO'],
    funnelTrack: 'solar' as FunnelTrack,
    displayName: 'Xcel Energy',
    emailIsUsername: false,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.co,
    zipCode: '80002',
    products: vnmElegibleUtilityProducts,
    fieldValidationRules: [],
  },
};

export const fetchUtilityResponse = (name: keyof typeof TEST_UTILITIES): FetchUtilityResponse => ({
  id: TEST_UTILITIES[name].id,
  displayName: TEST_UTILITIES[name].displayName,
  funnelTrack: TEST_UTILITIES[name].funnelTrack,
  state: TEST_UTILITIES[name].state,
  fieldValidationRules: TEST_UTILITIES[name].fieldValidationRules || [],
});
